package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.render.model.ImageStyle
import top.wcpe.allincore.ui.runtime.render.model.ImageStyleMode
import kotlin.jvm.JvmStatic

/**
 * 图片元素：以资源标识符形式引用，具体加载交由渲染层处理。
 *
 * 支持两套互斥的 UV 能力，渲染时由 ImageRenderer 按优先级路由：
 * - UV 变换（uvRotation/uvScaleX/uvScaleY/uvOffsetX/uvOffsetY/imageMode）：任一字段非默认值时走 GPU
 *   image_styled shader，由 PropAppliers 写入。
 * - UV 切片（uvX/uvY/uvW/uvH）：uvW 与 uvH 同时 >0 时从大图取子区域，走 drawTexturedQuad 路径。
 */
class Image private constructor() : UIElement<Image>() {

    private var resourceValue: String? = ""

    // 贴图 UV 子区域（像素坐标系）；uvW/uvH 同时 >0 时才视为启用 UV 切片
    private var uvXValue: Float = DEFAULT_COORD
    private var uvYValue: Float = DEFAULT_COORD
    private var uvWValue: Float = DEFAULT_COORD
    private var uvHValue: Float = DEFAULT_COORD

    // UV 变换样式（IMAGE_STYLED）
    private var uvRotationValue: Float = DEFAULT_COORD
    private var uvScaleXValue: Float = DEFAULT_SCALE
    private var uvScaleYValue: Float = DEFAULT_SCALE
    private var uvOffsetXValue: Float = DEFAULT_COORD
    private var uvOffsetYValue: Float = DEFAULT_COORD
    private var imageModeValue: ImageStyleMode = ImageStyleMode.NORMAL

    // 着色覆盖（ARGB）；null 时不叠加
    private var tintColorValue: Int? = null

    protected override fun self(): Image = this

    fun resource(resource: String?): Image {
        resourceValue = resource
        return this
    }

    fun resource(): String? = resourceValue

    fun uvX(v: Float): Image {
        uvXValue = v
        return this
    }

    fun uvX(): Float = uvXValue

    fun uvY(v: Float): Image {
        uvYValue = v
        return this
    }

    fun uvY(): Float = uvYValue

    fun uvW(v: Float): Image {
        uvWValue = v
        return this
    }

    fun uvW(): Float = uvWValue

    fun uvH(v: Float): Image {
        uvHValue = v
        return this
    }

    fun uvH(): Float = uvHValue

    /** uvW 与 uvH 同时大于 0 才视为启用 UV 切片。 */
    fun hasUv(): Boolean = uvWValue > DEFAULT_COORD && uvHValue > DEFAULT_COORD

    fun uvRotation(r: Float): Image {
        uvRotationValue = r
        return this
    }

    fun uvRotation(): Float = uvRotationValue

    fun uvScaleX(s: Float): Image {
        uvScaleXValue = s
        return this
    }

    fun uvScaleX(): Float = uvScaleXValue

    fun uvScaleY(s: Float): Image {
        uvScaleYValue = s
        return this
    }

    fun uvScaleY(): Float = uvScaleYValue

    fun uvOffsetX(o: Float): Image {
        uvOffsetXValue = o
        return this
    }

    fun uvOffsetX(): Float = uvOffsetXValue

    fun uvOffsetY(o: Float): Image {
        uvOffsetYValue = o
        return this
    }

    fun uvOffsetY(): Float = uvOffsetYValue

    fun imageMode(m: ImageStyleMode): Image {
        imageModeValue = m
        return this
    }

    fun imageMode(): ImageStyleMode = imageModeValue

    /** 着色覆盖；null 时不叠加。 */
    fun tintColor(v: Int?): Image {
        tintColorValue = v
        return this
    }

    fun tintColor(): Int? = tintColorValue

    /** 是否含非默认 UV 变换（任一字段偏离默认）。 */
    fun hasUvStyle(): Boolean = imageModeValue != ImageStyleMode.NORMAL || hasNonDefaultUvTransform()

    // 拆分多重 || 以满足 ComplexCondition 阈值：缩放/偏移各自聚合，再与旋转/模式合并
    private fun hasNonDefaultUvTransform(): Boolean {
        val scaled = uvScaleXValue != DEFAULT_SCALE || uvScaleYValue != DEFAULT_SCALE
        val offset = uvOffsetXValue != DEFAULT_COORD || uvOffsetYValue != DEFAULT_COORD
        return uvRotationValue != DEFAULT_COORD || scaled || offset
    }

    /** 构造与当前状态等价的 ImageStyle 值对象。 */
    fun toImageStyle(): ImageStyle = ImageStyle.builder()
        .uvRotation(uvRotationValue)
        .uvScaleX(uvScaleXValue)
        .uvScaleY(uvScaleYValue)
        .uvOffsetX(uvOffsetXValue)
        .uvOffsetY(uvOffsetYValue)
        .mode(imageModeValue)
        .build()

    companion object {
        /** UV 坐标 / 旋转 / 偏移类字段默认值（0）。 */
        private const val DEFAULT_COORD = 0f

        /** UV 缩放字段默认值（1，即不缩放）。 */
        private const val DEFAULT_SCALE = 1f

        /** 创建一个图片元素。 */
        @JvmStatic
        fun create(): Image = Image()
    }
}
